import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { Lock, Mail, User, AlertCircle, CheckCircle } from 'lucide-react';
import { Logo } from '@/components/quickscan/Logo';
const AuthPage = () => {
  const navigate = useNavigate();
  const {
    signIn,
    signUp,
    resetPassword
  } = useAuth();
  const {
    toast
  } = useToast();

  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const [loginLoading, setLoginLoading] = useState(false);

  // Register form state
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [registerLoading, setRegisterLoading] = useState(false);

  // Reset password state
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  // Recovery flow state
  const [isRecovery, setIsRecovery] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [showRecoveryDialog, setShowRecoveryDialog] = useState(false);

  // Check for recovery or invitation session on mount
  useEffect(() => {
    const checkRecoverySession = async () => {
      try {
        // Check URL parameters first - DON'T clean URL immediately
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const type = urlParams.get('type') || hashParams.get('type');
        const isRecoveryFromUrl = type === 'recovery';
        const isInviteFromUrl = type === 'invite' || type === 'signup';
        if (isRecoveryFromUrl) {
          console.log('Recovery detected from URL');
          setIsRecovery(true);
          setShowRecoveryDialog(true);
          return;
        }
        if (isInviteFromUrl) {
          console.log('Invitation signup detected from URL');
          // Show a message about setting up account
          // The user will need to create a password
          return;
        }
        const {
          data: {
            session
          },
          error
        } = await supabase.auth.getSession();
        if (error) {
          console.error('Session error:', error);
          return;
        }

        // If we have a session and are on auth page, redirect to root
        if (session?.user && !isRecoveryFromUrl && !isInviteFromUrl) {
          navigate('/');
        }
      } catch (error) {
        console.error('Recovery check error:', error);
      }
    };
    checkRecoverySession();
  }, [navigate]);

  // Listen for auth state changes
  useEffect(() => {
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth event:', event);
      if (event === 'PASSWORD_RECOVERY') {
        console.log('Password recovery event detected');
        setIsRecovery(true);
        setShowRecoveryDialog(true);
      } else if (event === 'SIGNED_IN' && !isRecovery) {
        console.log('Sign in event, redirecting');
        navigate('/');
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate, isRecovery]);
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      await signIn(loginData.email, loginData.password);
      toast({
        title: 'Welkom terug!',
        description: 'Je bent succesvol ingelogd.'
      });
      navigate('/');
    } catch (error) {
      // Error is already handled in useAuth
    } finally {
      setLoginLoading(false);
    }
  };
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (registerData.password !== registerData.confirmPassword) {
      toast({
        title: 'Wachtwoorden komen niet overeen',
        description: 'Controleer je wachtwoorden en probeer opnieuw.',
        variant: 'destructive'
      });
      return;
    }
    if (registerData.password.length < 6) {
      toast({
        title: 'Wachtwoord te kort',
        description: 'Je wachtwoord moet minimaal 6 karakters bevatten.',
        variant: 'destructive'
      });
      return;
    }
    setRegisterLoading(true);
    try {
      await signUp(registerData.email, registerData.password);
      toast({
        title: 'Account aangemaakt!',
        description: 'Controleer je e-mail voor de bevestigingslink.'
      });
      // Reset form
      setRegisterData({
        email: '',
        password: '',
        confirmPassword: ''
      });
    } catch (error) {
      // Error is already handled in useAuth
    } finally {
      setRegisterLoading(false);
    }
  };
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      await resetPassword(resetEmail);
      toast({
        title: 'Reset link verstuurd',
        description: 'Controleer je e-mail voor de wachtwoord reset link.'
      });
      setResetDialogOpen(false);
      setResetEmail('');
    } catch (error) {
      // Error is already handled in useAuth
    } finally {
      setResetLoading(false);
    }
  };
  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast({
        title: 'Wachtwoord te kort',
        description: 'Minimaal 6 karakters vereist.',
        variant: 'destructive'
      });
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast({
        title: 'Wachtwoorden komen niet overeen',
        description: 'Controleer je invoer en probeer opnieuw.',
        variant: 'destructive'
      });
      return;
    }
    setRecoveryLoading(true);
    try {
      const {
        error
      } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (error) {
        throw error;
      }
      toast({
        title: 'Wachtwoord ingesteld',
        description: 'Je nieuwe wachtwoord is actief.'
      });

      // Clean URL and close dialog
      window.history.replaceState({}, document.title, '/auth');
      setShowRecoveryDialog(false);
      setIsRecovery(false);
      setNewPassword('');
      setConfirmNewPassword('');

      // Navigate to root - let RootRedirect determine the correct route
      navigate('/');
    } catch (error: any) {
      console.error('Password update error:', error);
      toast({
        title: 'Fout bij wachtwoord instellen',
        description: error.message || 'Er is een onbekende fout opgetreden.',
        variant: 'destructive'
      });
    } finally {
      setRecoveryLoading(false);
    }
  };
  return <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <Logo className="h-12 object-contain" />
          </div>
          
          <CardDescription className="text-center"> </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="login" className="space-y-4">
            
            
            <TabsContent value="login" className="space-y-4">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">E-mailadres</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input id="login-email" type="email" placeholder="naam@bedrijf.nl" value={loginData.email} onChange={e => setLoginData({
                    ...loginData,
                    email: e.target.value
                  })} className="pl-10" required />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Wachtwoord</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input id="login-password" type="password" placeholder="••••••••" value={loginData.password} onChange={e => setLoginData({
                    ...loginData,
                    password: e.target.value
                  })} className="pl-10" required />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="link" className="px-0 text-sm">
                        Wachtwoord vergeten?
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Wachtwoord opnieuw instellen</DialogTitle>
                        <DialogDescription>
                          Voer je e-mailadres in en we sturen je een link om je wachtwoord opnieuw in te stellen.
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleResetPassword} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="reset-email">E-mailadres</Label>
                          <Input id="reset-email" type="email" placeholder="naam@bedrijf.nl" value={resetEmail} onChange={e => setResetEmail(e.target.value)} required />
                        </div>
                        <div className="flex justify-end space-x-2">
                          <Button type="button" variant="outline" onClick={() => setResetDialogOpen(false)}>
                            Annuleren
                          </Button>
                          <Button type="submit" disabled={resetLoading}>
                            {resetLoading ? 'Versturen...' : 'Reset link versturen'}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
                <Button type="submit" className="w-full" disabled={loginLoading}>
                  {loginLoading ? 'Inloggen...' : 'Inloggen'}
                </Button>
              </form>
            </TabsContent>
            
          </Tabs>
        </CardContent>
      </Card>

      {/* Recovery Password Dialog */}
      <Dialog open={showRecoveryDialog} onOpenChange={setShowRecoveryDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nieuw wachtwoord instellen</DialogTitle>
            <DialogDescription>
              Stel een nieuw wachtwoord in om verder te gaan
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePasswordUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password-dialog">Nieuw wachtwoord</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input id="new-password-dialog" type="password" placeholder="Minimaal 6 karakters" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="pl-10" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-new-password-dialog">Bevestig nieuw wachtwoord</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input id="confirm-new-password-dialog" type="password" placeholder="Herhaal je wachtwoord" value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} className="pl-10" required />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => {
              setShowRecoveryDialog(false);
              window.history.replaceState({}, document.title, '/auth');
            }}>
                Annuleren
              </Button>
              <Button type="submit" disabled={recoveryLoading}>
                {recoveryLoading ? 'Wachtwoord instellen...' : 'Wachtwoord instellen'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>;
};
export default AuthPage;